import cx from 'classnames';
type OtovoLogoVariant = 'blue' | 'white';

type LogoProps = {
  variant?: OtovoLogoVariant;
} & React.SVGProps<SVGSVGElement>;

const VARIANT_COLOR_MAP: Record<LogoProps['variant'], string> = {
  blue: 'fill-blue-60 transition-all duration-200',
  white: 'fill-white transition-all duration-200',
};

const OtovoLogo = ({ variant = 'blue', ...svgProps }: LogoProps) => {
  const baseClasses = VARIANT_COLOR_MAP[variant];

  return (
    <>
      {/* Only the 'O' with the sun */}
      <svg
        className={cx(baseClasses, 'h-[34px] w-[50px] sm:hidden')}
        viewBox="0 0 52 48"
        xmlns="http://www.w3.org/2000/svg"
        {...svgProps}
      >
        <path d="m6.7291 33.172c-1.6198-0.8813-2.9636-2.1808-3.8859-3.7581-0.95406-1.6075-1.4311-3.4176-1.4311-5.4302s0.47704-3.8174 1.4311-5.4143c0.92586-1.5702 2.2691-2.8634 3.8859-3.7411 1.655-0.883 3.5086-1.3456 5.392-1.3456 1.8835 0 3.737 0.4626 5.3921 1.3456 1.6144 0.8757 2.9532 2.1697 3.8708 3.7411 0.9441 1.5969 1.4161 3.4017 1.4161 5.4143s-0.472 3.8227-1.4161 5.4302c-0.9143 1.5786-2.2541 2.8789-3.872 3.7581-1.655 0.8831-3.5086 1.3456-5.392 1.3456-1.8835 0-3.7358-0.4625-5.3909-1.3456zm9.0817-2.5156c1.0918-0.6286 1.9758-1.5532 2.546-2.6629 0.6155-1.1541 0.9233-2.4907 0.9233-4.0098s-0.3078-2.8504-0.9233-3.9938c-0.5712-1.1015-1.4556-2.0163-2.546-2.6335-1.0833-0.613-2.3182-0.9194-3.7047-0.9194s-2.6214 0.3057-3.7047 0.9171c-1.0907 0.6178-1.9752 1.5335-2.546 2.6358-0.61553 1.1442-0.92329 2.4755-0.92329 3.9938 0 1.5184 0.30776 2.8549 0.92329 4.0098 0.57023 1.1097 1.4542 2.0343 2.546 2.6629 1.0841 0.6213 2.319 0.9319 3.7047 0.9319s2.6206-0.3106 3.7047-0.9319z" />
        <path
          d="M27.6331 0H24.1996V13.4814H27.6331V0Z"
          fill="var(--color-peach-50)"
        />
        <path
          d="m44.423 8.2259-2.4278-2.3848-9.7049 9.5328 2.4278 2.3848 9.7049-9.5328z"
          fill="var(--color-peach-50)"
        />
        <path
          d="m50.365 25.702v-3.3726h-13.725v3.3726h13.725z"
          fill="var(--color-peach-50)"
        />
        <path
          d="m41.993 42.19 2.4278-2.3849-9.7048-9.5328-2.4278 2.3848 9.7048 9.5329z"
          fill="var(--color-peach-50)"
        />
        <path
          d="m27.633 34.518h-3.4335v13.481h3.4335v-13.481z"
          fill="var(--color-peach-50)"
        />
      </svg>
      {/* The full logo with 'OTOVO' text */}
      <svg
        className={cx(baseClasses, 'hidden h-[44.5px] w-[120px] sm:block')}
        viewBox="0 0 111 40"
        xmlns="http://www.w3.org/2000/svg"
        {...svgProps}
      >
        <path d="m4.4442 27.644c-1.3541-0.7342-2.4774-1.8172-3.248-3.1317-0.79745-1.3396-1.1962-2.848-1.1962-4.5252s0.39873-3.1812 1.1962-4.512c0.77365-1.3086 1.8965-2.3863 3.248-3.1175 1.3735-0.749 2.9223-1.1355 4.4944-1.1214 1.5802-0.0158 3.1375 0.3706 4.5194 1.1214 1.3494 0.7297 2.4685 1.8081 3.2355 3.1175 0.7878 1.3308 1.1824 2.8348 1.1836 4.512 0.0013 1.6772-0.3932 3.1856-1.1836 4.5252-0.764 1.3153-1.8835 2.3989-3.2355 3.1317-1.3817 0.7512-2.9391 1.1377-4.5194 1.1214-1.5721 0.0143-3.121-0.3721-4.4944-1.1214zm7.5909-2.0963c0.9126-0.5239 1.6515-1.2944 2.1281-2.2192 0.5145-0.9617 0.7717-2.0755 0.7717-3.3414s-0.2572-2.3753-0.7717-3.3282c-0.4774-0.9179-1.2167-1.6803-2.1281-2.1946-0.9055-0.5108-1.9376-0.7662-3.0965-0.7662-1.1589 0-2.1914 0.2548-3.0975 0.7643-0.90928 0.5151-1.6465 1.2774-2.1223 2.1946-0.51449 0.9535-0.77173 2.0629-0.77173 3.3282s0.25724 2.3791 0.77173 3.3415c0.47507 0.924 1.2119 1.6944 2.1223 2.2191 0.90678 0.5177 1.9393 0.7765 3.0975 0.7765 1.1582 0 2.1904-0.2582 3.0965-0.7746z" />
        <path d="m32.034 11.456v2.2928h-5.1186v14.843h-2.8698v-14.843h-5.1446v-2.2928h13.133z" />
        <path d="m37.501 27.643c-1.3541-0.7342-2.4774-1.8172-3.248-3.1317-0.7975-1.3396-1.1962-2.848-1.1962-4.5252s0.3987-3.1812 1.1962-4.512c0.7736-1.3086 1.8964-2.3863 3.248-3.1175 1.3833-0.7359 2.9326-1.1214 4.5069-1.1214s3.1235 0.3855 4.5069 1.1214c1.3494 0.7297 2.4685 1.808 3.2355 3.1175 0.7897 1.3314 1.1846 2.8354 1.1846 4.512 0 1.6765-0.3946 3.1849-1.1837 4.5252-0.764 1.3153-1.8835 2.3989-3.2354 3.1317-1.3834 0.7359-2.9327 1.1213-4.5069 1.1213-1.5743 0-3.1236-0.3854-4.5069-1.1213h-1e-3zm7.5909-2.0963c0.9126-0.5239 1.6514-1.2944 2.1281-2.2192 0.5145-0.9617 0.7717-2.0755 0.7717-3.3414s-0.2572-2.3754-0.7717-3.3283c-0.4775-0.9178-1.2167-1.6802-2.1281-2.1945-0.9055-0.5108-1.9377-0.7662-3.0965-0.7662-1.1589 0-2.1914 0.2548-3.0976 0.7643-0.9108 0.5146-1.6497 1.2769-2.1271 2.1946-0.5144 0.9535-0.7717 2.0629-0.7717 3.3282s0.2573 2.3791 0.7717 3.3414c0.4764 0.9247 1.2149 1.6952 2.1271 2.2192 0.9068 0.5177 1.9393 0.7765 3.0976 0.7765 1.1582 0 2.1904-0.2582 3.0965-0.7746z" />
        <path d="m70.082 11.456-6.5722 17.136h-3.3233l-6.5964-17.136h3.0715l5.187 14.252 5.2092-14.252h3.0242z" />
        <path d="m74.527 27.643c-1.3539-0.7344-2.4771-1.8174-3.248-3.1317-0.7974-1.3396-1.1962-2.848-1.1962-4.5252s0.3988-3.1812 1.1962-4.512c0.7739-1.3085 1.8966-2.3861 3.248-3.1175 1.3834-0.7359 2.9327-1.1214 4.5069-1.1214 1.5743 0 3.1236 0.3855 4.5069 1.1214 1.3494 0.7297 2.4685 1.808 3.2355 3.1175 0.7891 1.3308 1.1837 2.8348 1.1837 4.512s-0.3946 3.1856-1.1837 4.5252c-0.7642 1.3154-1.8841 2.3991-3.2364 3.1317-1.3834 0.7359-2.9327 1.1213-4.5069 1.1213-1.5743 0-3.1236-0.3854-4.5069-1.1213h9e-4zm7.591-2.0963c0.9125-0.5239 1.6514-1.2944 2.128-2.2192 0.5145-0.9617 0.7717-2.0755 0.7717-3.3414s-0.2572-2.3754-0.7717-3.3283c-0.4774-0.9178-1.2167-1.6802-2.128-2.1945-0.9055-0.5108-1.9377-0.7662-3.0966-0.7662s-2.1911 0.2548-3.0966 0.7643c-0.9116 0.5148-1.6509 1.2779-2.128 2.1964-0.5145 0.9536-0.7718 2.063-0.7718 3.3283s0.2573 2.3791 0.7718 3.3414c0.4766 0.9248 1.2155 1.6953 2.128 2.2192 0.9062 0.5177 1.9384 0.7765 3.0966 0.7765s2.1904-0.2588 3.0966-0.7765z" />
        <path
          d="M91.9994 0H89.1295V11.2345H91.9994V0Z"
          fill="var(--color-peach-50)"
        />
        <path
          d="m106.03 6.855-2.029-1.9873-8.1123 7.944 2.0294 1.9873 8.1119-7.944z"
          fill="var(--color-peach-50)"
        />
        <path
          d="M111 21.4179V18.6074H99.5282V21.4179H111Z"
          fill="var(--color-peach-50)"
        />
        <path
          d="m104 35.158 2.029-1.9873-8.1116-7.9441-2.0293 1.9874 8.1119 7.944z"
          fill="var(--color-peach-50)"
        />
        <path
          d="m91.999 28.765h-2.8699v11.234h2.8699v-11.234z"
          fill="var(--color-peach-50)"
        />
      </svg>
    </>
  );
};
export default OtovoLogo;
